import React, { useEffect, useState } from "react";
import '../Style/Notification.css';
import judicialLogo from '../Images/judicialLogo.png';
import { useDispatch, useSelector } from "react-redux";
import { getDeviceToken_User } from "../Redux/Dashboard/DashboardSlice";
import Spinner from "../Spinner/Spinner";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faPaperPlane, faComment } from '@fortawesome/free-solid-svg-icons';
import { IoCloseCircleOutline } from "react-icons/io5";
import { sendNotification } from "../Redux/Auth/AuthSlice";
import { Notify } from "../CommonPopup/Popup";

const Notification = () => {
    const dispatch = useDispatch();
    const selector = useSelector((s) => s.DashboardSlice);
    const getTokenUser = selector.getTokenUser || [];

    const loader = selector.loader;
    const [currentPage, setCurrentPage] = useState(1);
    const studentsPerPage = 8;
    const [searchTerm, setSearchTerm] = useState("");
    const [activeTab, setActiveTab] = useState("All");
    const [showActionColumn, setShowActionColumn] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [showMessageModal, setShowMessageModal] = useState(false);
    const [messageForm, setMessageForm] = useState({ title: "Dev Judicial Academy", message: "" });

    useEffect(() => {
        dispatch(getDeviceToken_User());
    }, [dispatch]);

    const filteredStudents = getTokenUser
        ?.filter((student) => {
            const name = student.name || '';
            const email = student.email || '';
            const phone = student.phone || '';
            return (
                name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                phone.includes(searchTerm)
            );
        })
        .filter((student) => {
            if (activeTab === "All") return true;
            return activeTab === "Paid" ? student.paid_account : !student.paid_account;
        })
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) || [];

    const totalPages = Math.ceil(filteredStudents.length / studentsPerPage);
    const indexOfLastStudent = currentPage * studentsPerPage;
    const indexOfFirstStudent = indexOfLastStudent - studentsPerPage;
    const currentStudents = filteredStudents.slice(indexOfFirstStudent, indexOfLastStudent);

    const nextPage = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };

    const prevPage = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };

    const handleSendNotificationClick = () => {
        setShowActionColumn(true);
        setSelectedUsers([]); // Set to empty array to uncheck all users by default
    };

    const handleSelectAll = () => {
        // Select all users from filteredStudents (across all pages)
        const allUserIds = filteredStudents.map(student => student._id);
        setSelectedUsers([...new Set(allUserIds)]);
    };

    const handleUnselectAll = () => {
        // Clear all selected users
        setSelectedUsers([]);
    };

    const handleSelectUser = (userId) => {
        setSelectedUsers(prev =>
            prev.includes(userId)
                ? prev.filter(id => id !== userId)
                : [...prev, userId]
        );
    };

    const handleSendMessage = () => {
        if (selectedUsers.length === 0) {
            Notify({
                data: "error",
                msg: "Please select at least one user to send the notification.",
                type: "error"
            });
            return;
        }

        const selectedUserData = getTokenUser
            .filter(user => selectedUsers.includes(user._id));

        const deviceTokens = selectedUserData
            .map(user => user.device_token)
            .filter(token => token); // Filter out null/undefined tokens

        const userIds = selectedUserData
            .map(user => user._id)
            .filter(id => id); // Ensure valid user IDs

        if (deviceTokens.length === 0 || userIds.length === 0 || deviceTokens.length !== userIds.length) {
            Notify({
                data: "error",
                msg: "No valid device tokens or user IDs found, or they don't match in length.",
                type: "error"
            });
            return;
        }

        const payload = {
            deviceTokens,
            userIds,
            title: messageForm.title.trim(),
            message: messageForm.message.trim()
        };

        dispatch(sendNotification(payload))
            .unwrap()
            .then((data) => {
                Notify({
                    data: "success",
                    msg: "Notifications Sent Successfully",
                    type: "success"
                });
                dispatch(getDeviceToken_User());
            })
            .catch((err) => {
                Notify({
                    data: "error",
                    msg: "Failed to send Notifications",
                    type: "error"
                });
            })
            .finally(() => {
                setShowActionColumn(false);
                setShowMessageModal(false);
                setSelectedUsers([]);
                setMessageForm({ title: "Dev Judicial Academy", message: "" });
                dispatch(getDeviceToken_User());
            });
    };

    return (
        <div className="notification-panel-main">
            <img className="notification-judicial-logo" src={judicialLogo} alt="Judicial Logo" />
            <h2 className="text-white"> Active Students ({filteredStudents.length})</h2>

            <div className="notification-tabs">
                <button
                    className={`notification-tab ${activeTab === "All" ? "active" : ""}`}
                    onClick={() => {
                        setActiveTab("All");
                        setCurrentPage(1);
                    }}
                >
                    All
                </button>
                <button
                    className={`notification-tab ${activeTab === "Paid" ? "active" : ""}`}
                    onClick={() => {
                        setActiveTab("Paid");
                        setCurrentPage(1);
                    }}
                >
                    Paid
                </button>
                <button
                    className={`notification-tab ${activeTab === "Free" ? "active" : ""}`}
                    onClick={() => {
                        setActiveTab("Free");
                        setCurrentPage(1);
                    }}
                >
                    Free
                </button>
            </div>

            <div className="notification-controls">
                <div className="notification-search-container">
                    <FontAwesomeIcon icon={faSearch} className="notification-search-icon" />
                    <input
                        type="text"
                        placeholder="Search by name, email, or phone..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="notification-search-input"
                    />
                </div>
                <button
                    className="notification-action-btn"
                    onClick={showActionColumn ? () => setShowMessageModal(true) : handleSendNotificationClick}
                >
                    <FontAwesomeIcon icon={showActionColumn ? faComment : faPaperPlane} />
                    {showActionColumn ? "Add Message" : "Send Notification"}
                </button>
            </div>

            <div className="notification-table-container">
                <table className="notification-students-table">
                    <thead>
                        <tr>
                            <th>Created At</th>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                            {showActionColumn && (
                                <th>
                                    Action
                                    <div className="notification-action-buttons">
                                        <button
                                            className="notification-select-all-btn"
                                            onClick={handleSelectAll}
                                        >
                                            Select All
                                        </button>
                                        <button
                                            className="notification-unselect-all-btn"
                                            onClick={handleUnselectAll}
                                        >
                                            Unselect All
                                        </button>
                                    </div>
                                </th>
                            )}
                        </tr>
                    </thead>
                    {loader ? (
                        <tbody>
                            <tr>
                                <td colSpan={showActionColumn ? 6 : 5}>
                                    <div className="notification-spinner-wrapper">
                                        <Spinner />
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    ) : (
                        <tbody>
                            {currentStudents.length > 0 ? (
                                currentStudents.map((student, index) => (
                                    <tr key={index}>
                                        <td>{new Date(student.created_at).toLocaleDateString()}</td>
                                        <td>{student.name}</td>
                                        <td>{student.email}</td>
                                        <td>{student.phone}</td>
                                        {showActionColumn && (
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedUsers.includes(student._id)}
                                                    onChange={() => handleSelectUser(student._id)}
                                                />
                                            </td>
                                        )}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={showActionColumn ? 6 : 5} className="notification-no-data">
                                        No active student found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    )}
                </table>
            </div>

            {!loader && totalPages > 1 && (
                <div className="notification-pagination">
                    <button onClick={prevPage} disabled={currentPage === 1}>
                        Prev
                    </button>
                    <span>Page {currentPage} of {totalPages}</span>
                    <button onClick={nextPage} disabled={currentPage === totalPages}>
                        Next
                    </button>
                </div>
            )}

            {showMessageModal && (
                <>
                    <div className="notification-modal-overlay">
                        {
                            loader ? <div
                                style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    height: "100%",
                                    minHeight: "300px",
                                }}
                            >
                                <Spinner />
                            </div>
                                :
                                <div className="notification-modal-content">
                                    <div className="notification-modal-header">
                                        <h5>Send Message</h5>
                                        <IoCloseCircleOutline
                                            size={28}
                                            onClick={() => setShowMessageModal(false)}
                                            style={{ cursor: "pointer", color: "#f44336" }}
                                        />
                                    </div>
                                    <div className="notification-modal-body">
                                        <div className="notification-form-group">
                                            <label>Title</label>
                                            <input
                                                type="text"
                                                value={messageForm.title}
                                                onChange={(e) => setMessageForm({ ...messageForm, title: e.target.value })}
                                                className="notification-form-input"
                                            />
                                        </div>
                                        <div className="notification-form-group">
                                            <label>Message</label>
                                            <textarea
                                                value={messageForm.message}
                                                onChange={(e) => setMessageForm({ ...messageForm, message: e.target.value })}
                                                className="notification-form-textarea"
                                            />
                                        </div>
                                    </div>
                                    <div className="notification-modal-footer">
                                        <button
                                            className="notification-btn notification-btn-secondary"
                                            onClick={() => setShowMessageModal(false)}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            className="notification-btn notification-btn-primary"
                                            onClick={handleSendMessage}
                                            disabled={!messageForm.title.trim() || !messageForm.message.trim() || selectedUsers.length === 0}
                                        >
                                            Send
                                        </button>
                                    </div>
                                </div>
                        }
                    </div>
                </>
            )}
        </div>
    );
};

export default Notification;